// CollectionModal.js - Consolidated modal for collection and release selection
import React, { useState, useEffect } from 'react';
import { Package, Plus, FileText, Minus } from 'lucide-react';
import { useMediaDataContext } from '../contexts/MediaDataContext';
import BaseModal from './BaseModal';
import { useNotification } from '../contexts/NotificationContext';

const CollectionModal = ({
  mode, // 'collection', 'release', or 'unified'
  isOpen,
  onClose,

  // Collection mode props
  selectedMasterItem,
  availableReleases = [],
  onSuccess = () => {},

  // Release mode props
  movie,
  supabase,
  currentUser,
  onReleaseSelected,

  // Unified mode props
  removeFromCollection,
}) => {
  const { userCollectionItems, addToCollectionWithRelease, addToCollection, loadUserCollection } =
    useMediaDataContext();

  const isCollectionMode = mode === 'collection';
  const isReleaseMode = mode === 'release';
  const isUnifiedMode = mode === 'unified';
  const { showSuccess, showError } = useNotification();

  // Collection mode state
  const [selectedReleases, setSelectedReleases] = useState([]);
  const [releaseNotes, setReleaseNotes] = useState({});

  // Release mode state
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [personalNotes, setPersonalNotes] = useState('');

  // Unified mode state
  const [addedItems, setAddedItems] = useState([]); // Track locally added items
  const [removedItems, setRemovedItems] = useState([]); // Track locally removed items

  // Release mode and unified mode: Load releases for movie
  useEffect(() => {
    if ((!isReleaseMode && !isUnifiedMode) || !movie?.id) return;

    const loadReleases = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('movie_releases')
          .select('*')
          .eq('master_movie_id', movie.id)
          .order('release_year', { ascending: false });

        if (error) {
          console.error('Error loading releases:', error);
        } else {
          setReleases(data || []);
          // Auto-select first release if only one exists
          if (data && data.length === 1) {
            setSelectedRelease(data[0]);
          }
        }
      } catch (err) {
        console.error('Error loading releases:', err);
      } finally {
        setLoading(false);
      }
    };

    loadReleases();
  }, [isReleaseMode, isUnifiedMode, movie?.id, supabase]);

  const handleClose = () => {
    // Reset collection mode state
    setSelectedReleases([]);
    setReleaseNotes({});

    // Reset release mode state
    setSelectedRelease(null);
    setPersonalNotes('');

    onClose();
  };

  // Collection mode handlers
  const handleReleaseClick = release => {
    setSelectedReleases(prev => {
      const isSelected = prev.some(r => r.id === release.id);
      if (isSelected) {
        // Remove from selection
        const newReleases = prev.filter(r => r.id !== release.id);
        // Also remove any notes for this release
        const newNotes = { ...releaseNotes };
        delete newNotes[release.id];
        setReleaseNotes(newNotes);
        return newReleases;
      } else {
        // Add to selection
        return [...prev, release];
      }
    });
  };

  const handleNotesChange = (releaseId, notes) => {
    setReleaseNotes(prev => ({
      ...prev,
      [releaseId]: notes,
    }));
  };

  const handleAddToCollection = async () => {
    if (isCollectionMode) {
      if (availableReleases.length > 0 && selectedReleases.length === 0) {
        showError('Please select at least one release to add to your collection');
        return;
      }

      try {
        if (selectedReleases.length > 0) {
          // Add each selected release to collection with its individual notes
          for (const release of selectedReleases) {
            const individualNotes = releaseNotes[release.id] || '';
            await addToCollectionWithRelease(release.id, individualNotes);
          }
        } else {
          await addToCollection(selectedMasterItem);
        }

        handleClose();
        onSuccess();
      } catch (error) {
        console.error('Error adding to collection:', error);
        showError('Error adding to collection: ' + error.message);
      }
    } else if (isReleaseMode) {
      if (!selectedRelease) {
        showError('Please select a release');
        return;
      }

      onReleaseSelected(selectedRelease.id, personalNotes);
      handleClose();
    }
  };

  // Unified mode helper functions
  const isReleaseInCollection = releaseDisplayId => {
    const inOriginalCollection =
      userCollectionItems?.some(item => item.movie_releases?.display_id === releaseDisplayId) ||
      false;
    const wasRemoved = removedItems.includes(releaseDisplayId);
    const wasAdded = addedItems.includes(releaseDisplayId);

    return (inOriginalCollection && !wasRemoved) || wasAdded;
  };

  const handleAddToCollectionUnified = releaseDisplayId => {
    // Track locally added items for immediate UI feedback
    setAddedItems(prev => [...prev, releaseDisplayId]);
    // Also remove from removedItems if it was previously removed
    setRemovedItems(prev => prev.filter(id => id !== releaseDisplayId));

    // Add to actual collection
    const release = releases.find(r => r.display_id === releaseDisplayId);
    if (release && addToCollection) {
      addToCollection(release.id, ''); // Add with empty notes for now
      loadUserCollection(); // Reload collection data
    }
  };

  const handleRemoveFromCollectionUnified = async (releaseDisplayId, releaseTitle) => {
    // Update local state immediately for UI feedback
    if (addedItems.includes(releaseDisplayId)) {
      // If it was locally added, just remove it from addedItems
      setAddedItems(prev => prev.filter(id => id !== releaseDisplayId));
    } else {
      // If it was in original collection, mark as removed
      setRemovedItems(prev => [...prev, releaseDisplayId]);
    }

    try {
      // Find the collection item and remove it
      const collectionItem = userCollectionItems?.find(
        item => item.movie_releases?.display_id === releaseDisplayId
      );

      if (collectionItem && removeFromCollection) {
        const result = await removeFromCollection(collectionItem.id);
        if (!result || !result.success) {
          // Revert local state changes on error
          if (addedItems.includes(releaseDisplayId)) {
            setAddedItems(prev => [...prev, releaseDisplayId]);
          } else {
            setRemovedItems(prev => prev.filter(id => id !== releaseDisplayId));
          }
          showError(`Error removing ${releaseTitle} from collection`);
        } else {
          loadUserCollection(); // Reload collection data
        }
      }
    } catch (error) {
      console.error('Error removing from collection:', error);
      // Revert local state changes on error
      if (addedItems.includes(releaseDisplayId)) {
        setAddedItems(prev => [...prev, releaseDisplayId]);
      } else {
        setRemovedItems(prev => prev.filter(id => id !== releaseDisplayId));
      }
      showError(`Error removing ${releaseTitle} from collection`);
    }
  };

  // Get current releases to display
  const currentReleases = isUnifiedMode
    ? releases
    : isCollectionMode
      ? availableReleases
      : releases;
  const currentLoading = isReleaseMode || isUnifiedMode ? loading : false;

  const modalTitle = isCollectionMode
    ? 'Add to Collection'
    : isReleaseMode
      ? 'Select Release'
      : 'Manage Collection';
  const currentMovie = isCollectionMode ? selectedMasterItem : movie;

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} title={modalTitle} maxWidth="2xl">
      <div className="mb-6">
        <p className="text-gray-600">
          {currentMovie?.title} ({currentMovie?.year})
        </p>
        {isCollectionMode && (
          <p className="text-sm text-gray-500 mt-1">
            Select which releases you want to add to your collection
          </p>
        )}
        {isReleaseMode && (
          <p className="text-sm text-gray-500 mt-1">Choose a release to add to your collection</p>
        )}
      </div>

      {/* Collection and Release Mode Content */}
      {!isUnifiedMode &&
        (currentLoading ? (
          <div className="text-center py-8">
            <div className="text-gray-500">Loading releases...</div>
          </div>
        ) : currentReleases.length === 0 ? (
          <div className="text-center py-8">
            <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <div className="text-gray-500 mb-2">No releases found for this movie</div>
            <p className="text-sm text-gray-400">
              Releases must be added to the catalog before they can be collected.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-700 mb-4">
              {isCollectionMode
                ? `Select releases to add to your collection (${selectedReleases.length} selected):`
                : 'Select a release to add to your collection:'}
            </div>

            {/* Releases List */}
            <div className="space-y-3 max-h-64 overflow-y-auto dark-scrollbar">
              {currentReleases.map(release => {
                const isSelected = isCollectionMode
                  ? selectedReleases.some(r => r.id === release.id)
                  : selectedRelease?.id === release.id;

                return (
                  <div
                    key={release.id}
                    onClick={() => {
                      if (isCollectionMode) {
                        handleReleaseClick(release);
                      } else {
                        setSelectedRelease(release);
                      }
                    }}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-purple-300 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-200 hover:bg-purple-25'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900">
                            {release.release_title || 'Standard Release'}
                          </h4>
                          {isSelected && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                              Selected
                            </span>
                          )}
                        </div>

                        <div className="text-sm text-gray-600 space-y-1">
                          {release.studio && (
                            <div>
                              <span className="font-medium">Studio:</span> {release.studio}
                            </div>
                          )}
                          {release.release_year && (
                            <div>
                              <span className="font-medium">Year:</span> {release.release_year}
                            </div>
                          )}
                          {release.case_type && (
                            <div>
                              <span className="font-medium">Case:</span> {release.case_type}
                            </div>
                          )}
                        </div>

                        {release.release_notes && (
                          <p className="text-sm text-gray-700 mt-2 italic">
                            {release.release_notes}
                          </p>
                        )}
                      </div>

                      {/* Front cover image */}
                      <div className="ml-4 flex-shrink-0">
                        <div className="w-16 h-24 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                          {release.front_cover_url ? (
                            <img
                              src={release.front_cover_url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-gray-400 text-xs text-center">No Image</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Collection Mode - Individual Notes */}
                    {isCollectionMode && isSelected && (
                      <div className="mt-3 pt-3 border-t border-purple-100">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Personal Notes (optional)
                        </label>
                        <textarea
                          value={releaseNotes[release.id] || ''}
                          onChange={e => handleNotesChange(release.id, e.target.value)}
                          onClick={e => e.stopPropagation()}
                          className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-0 focus:border-red-500 focus:outline-none resize-none text-sm"
                          rows={2}
                          placeholder="Add personal notes about this release..."
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Release Mode - Global Notes Section */}
            {isReleaseMode && selectedRelease && (
              <div className="border-t pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Personal Notes (optional)
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <textarea
                    value={personalNotes}
                    onChange={e => setPersonalNotes(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-red-500 focus:outline-none resize-none"
                    rows={3}
                    placeholder="Add personal notes about this release..."
                  />
                </div>
              </div>
            )}
          </div>
        ))}

      {/* Unified Mode - All Releases with Add/Remove Actions */}
      {isUnifiedMode && (
        <>
          {currentLoading ? (
            <div className="text-center py-8">
              <div className="text-gray-500">Loading releases...</div>
            </div>
          ) : releases.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-2">No releases found for this movie</div>
              <p className="text-sm text-gray-400">
                Releases must be added to the catalog before they can be collected.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-700 mb-4">
                All releases for this movie - click + to add or - to remove from collection:
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto dark-scrollbar">
                {releases.map(release => {
                  const inCollection = isReleaseInCollection(release.display_id);
                  const collectionItem = userCollectionItems?.find(
                    item => item.movie_releases?.display_id === release.display_id
                  );

                  return (
                    <div key={release.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900">
                              {release.release_title || 'Standard Release'}
                            </h4>
                            {inCollection && (
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                                ✓ In Collection
                              </span>
                            )}
                          </div>

                          <div className="text-sm text-gray-600 space-y-1">
                            {release.studio && (
                              <div>
                                <span className="font-medium">Studio:</span> {release.studio}
                              </div>
                            )}
                            {release.release_year && (
                              <div>
                                <span className="font-medium">Year:</span> {release.release_year}
                              </div>
                            )}
                            {release.case_type && (
                              <div>
                                <span className="font-medium">Case:</span> {release.case_type}
                              </div>
                            )}
                            {inCollection && collectionItem?.created_at && (
                              <div>
                                <span className="font-medium">Added to Collection:</span>{' '}
                                {new Date(collectionItem.created_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>

                          {release.release_notes && (
                            <p className="text-sm text-gray-700 mt-2 italic">
                              {release.release_notes}
                            </p>
                          )}

                          {inCollection && collectionItem?.notes && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                              <span className="font-medium text-gray-700">Notes:</span>{' '}
                              {collectionItem.notes}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="ml-4 flex items-center space-x-2 flex-shrink-0">
                          {inCollection ? (
                            <button
                              onClick={() =>
                                handleRemoveFromCollectionUnified(
                                  release.display_id,
                                  release.release_title
                                )
                              }
                              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove from collection"
                            >
                              <Minus size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleAddToCollectionUnified(release.display_id)}
                              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                              title="Add to collection"
                            >
                              <Plus size={16} />
                            </button>
                          )}

                          {/* Front cover image */}
                          <div className="w-12 h-18 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                            {release.front_cover_url ? (
                              <img
                                src={release.front_cover_url}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="text-gray-400 text-xs text-center">No Image</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Actions */}
      <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
        <button
          onClick={handleClose}
          className={`px-4 py-2 rounded-lg transition-colors ${
            isUnifiedMode
              ? 'bg-gray-600 text-white hover:bg-gray-700'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          {isUnifiedMode ? 'Close' : 'Cancel'}
        </button>
        {!isUnifiedMode && (
          <button
            onClick={handleAddToCollection}
            disabled={
              currentLoading ||
              (isCollectionMode && availableReleases.length > 0 && selectedReleases.length === 0) ||
              (isReleaseMode && !selectedRelease)
            }
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={16} />
            Add to Collection
          </button>
        )}
      </div>
    </BaseModal>
  );
};

export default CollectionModal;
